import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError, consumePasswordReset, readPasswordReset } from '@/lib/api';

export function ResetPasswordPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const infoQ = useQuery({
    queryKey: ['password-reset', token],
    queryFn: () => readPasswordReset(token),
    retry: false,
  });

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('a confirmação não bate com a nova senha.');
      return;
    }
    setBusy(true);
    try {
      await consumePasswordReset(token, password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'validation_error')
          setError('a senha precisa de 12+ caracteres, com letra e dígito.');
        else if (err.code === 'reset_invalid') setError('link inválido, já usado ou expirado.');
        else if (err.code === 'rate_limited') setError('muitas tentativas — aguarde.');
        else setError(err.message);
      } else {
        setError('falha ao redefinir a senha.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-6 p-6">
      <h1 className="text-2xl font-semibold tracking-tight">Redefinir senha</h1>

      {infoQ.isLoading && <p className="text-sm text-slate-500">validando link…</p>}

      {infoQ.isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
          <p>Este link de redefinição é inválido, já foi usado ou expirou.</p>
          <p className="mt-2">
            Peça um novo ao dono do household e{' '}
            <Link to="/login" className="font-medium underline">
              volte ao login
            </Link>
            .
          </p>
        </div>
      )}

      {done && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
          Senha redefinida! Redirecionando para o login…
        </div>
      )}

      {infoQ.data && !done && (
        <form
          onSubmit={submit}
          className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
        >
          <p className="text-sm text-slate-600">
            Definindo nova senha para <strong>{infoQ.data.email_masked}</strong>.
          </p>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">Nova senha (mín. 12, letra + dígito)</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">Confirmar nova senha</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
            />
          </label>
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-slate-900 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? 'redefinindo…' : 'Redefinir senha'}
          </button>
        </form>
      )}
    </main>
  );
}
